import { useMemo, useState } from 'react';
import { useRankingsGridQuery } from '../api/queries';
import type { RankingsGridForKeyword } from '../types';

type Props = { af: string };

const DISPLAY_LABELS: Record<string, string> = {
  'senior care': 'Senior Care',
  'senior home care': 'Senior Home Care',
  'home care': 'Home Care',
};

// Color scale for a Maps 3-pack rank at a given grid cell.
function cellColor(rank: number | null): string {
  if (rank == null) return '#dc2626';          // not in 3-pack → red
  if (rank === 1) return '#1e3a8a';            // top → deep blue
  if (rank === 2) return '#2563eb';            // mid → blue
  return '#93c5fd';                            // #3 → light blue
}

function cellLabel(rank: number | null): string {
  if (rank == null) return '—';
  return `#${rank}`;
}

export default function RankingsGridMap({ af }: Props) {
  const { data, isLoading, error } = useRankingsGridQuery(af);
  const [activeKw, setActiveKw] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: '#8a95a5', fontSize: 13 }}>Loading geo-grid…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: '#dc2626', fontSize: 13 }}>
          Failed to load: {(error as Error).message}
        </div>
      </div>
    );
  }
  if (!data || data.refreshing || !data.keywords?.length) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: '#8a95a5', fontSize: 13 }}>
          {data?.refreshing
            ? 'Geo-grid is being computed in the background — this takes a couple minutes the first time. Check back shortly.'
            : 'Geo-grid not yet computed for this location.'}
        </div>
      </div>
    );
  }

  const selected = activeKw ?? data.keywords[0].keyword;

  return (
    <div style={{ padding: '8px 20px 20px' }}>
      <div className="ds-section-header">
        <h2>Maps 3-Pack Visibility — Geo Grid</h2>
        <span className="meta">
          {data.gridSize}×{data.gridSize} grid · ±{(data.halfWidthKm * 0.621371).toFixed(1)} mi from office · weekly refresh
        </span>
      </div>

      <div
        className="ds-panel"
        style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 220px', gap: 24 }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 16,
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: 8,
            }}
          >
            {data.keywords.map((k) => (
              <button
                key={k.keyword}
                onClick={() => setActiveKw(k.keyword)}
                style={{
                  border: 'none',
                  background: selected === k.keyword ? '#0f172a' : 'transparent',
                  color: selected === k.keyword ? '#fff' : '#475569',
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {DISPLAY_LABELS[k.keyword] ?? k.keyword}
              </button>
            ))}
          </div>

          <GridSquares
            kw={data.keywords.find((k) => k.keyword === selected)!}
            gridSize={data.gridSize}
          />
        </div>

        <SummaryPanel keywords={data.keywords} selected={selected} />
      </div>

      <div className="metrics-footer">
        Geo-grid via DataForSEO · Last refresh{' '}
        {data.checkedAt ? new Date(data.checkedAt).toLocaleString() : 'unknown'}
      </div>
    </div>
  );
}

function GridSquares({ kw, gridSize }: { kw: RankingsGridForKeyword; gridSize: number }) {
  // Build a row-major matrix.
  const matrix: ({ rank: number | null } | null)[][] = Array.from({ length: gridSize }, () =>
    Array(gridSize).fill(null),
  );
  for (const c of kw.cells) matrix[c.row][c.col] = { rank: c.mapsRank };
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
        gap: 4,
        maxWidth: 440,
      }}
    >
      {matrix.flatMap((row, r) =>
        row.map((cell, c) => {
          const rank = cell?.rank ?? null;
          return (
            <div
              key={`${r}-${c}`}
              title={`Row ${r + 1}, Col ${c + 1}: ${cellLabel(rank)}`}
              style={{
                aspectRatio: '1 / 1',
                background: cellColor(rank),
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                boxShadow: r === 2 && c === 2 ? '0 0 0 2px #0f172a' : undefined,
              }}
            >
              {cellLabel(rank)}
            </div>
          );
        }),
      )}
    </div>
  );
}

function SummaryPanel({
  keywords,
  selected,
}: {
  keywords: RankingsGridForKeyword[];
  selected: string;
}) {
  const stats = useMemo(() => {
    return keywords.map((k) => {
      const total = k.cells.length;
      const inPack = k.cells.filter((c) => c.mapsRank != null).length;
      const top1 = k.cells.filter((c) => c.mapsRank === 1).length;
      return { keyword: k.keyword, total, inPack, top1 };
    });
  }, [keywords]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Coverage by Keyword
        </div>
        {stats.map((s) => {
          const pct = s.total > 0 ? Math.round((s.inPack / s.total) * 100) : 0;
          const isSel = s.keyword === selected;
          return (
            <div
              key={s.keyword}
              style={{
                padding: '8px 10px',
                marginBottom: 4,
                borderRadius: 6,
                background: isSel ? '#f1f5f9' : 'transparent',
                fontSize: 13,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                  color: '#0f172a',
                  fontWeight: isSel ? 600 : 500,
                  textTransform: 'capitalize',
                }}
              >
                <span>{DISPLAY_LABELS[s.keyword] ?? s.keyword}</span>
                <span>
                  {s.inPack}/{s.total}
                </span>
              </div>
              <div
                style={{
                  height: 4,
                  background: '#e5e7eb',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: pct >= 50 ? '#2563eb' : pct >= 25 ? '#93c5fd' : '#dc2626',
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                In 3-pack at {pct}% of points · top-1 at {s.top1}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Legend
        </div>
        <Legend rank={1} label="Top of 3-pack" />
        <Legend rank={2} label="#2 in pack" />
        <Legend rank={3} label="#3 in pack" />
        <Legend rank={null} label="Not in pack" />
      </div>
    </div>
  );
}

function Legend({ rank, label }: { rank: number | null; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: '#475569',
        marginBottom: 4,
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 14,
          height: 14,
          borderRadius: 3,
          background: cellColor(rank),
        }}
      />
      {label}
    </div>
  );
}
